import * as companyManagementService from "../../services/company";
import * as settings from "../../services/settings";

import { Button, Col, Row, Spin, Table, notification } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useRef, useState } from "react";

import { ModalDelete } from "../model/modal/delete";
import { ModelCreateSales } from "./modal/modalCreateSales";
import permissions from "../../../configs/permission";
import { useAccessToken } from "../../contexts/AccessTokenContext";
import { usePermission } from "../../hooks/usePermission";

const initPage = { limit: 10, page: 1 };
export const CreateCompanySales = ({ t, detailData }) => {
  const hasFetchedRef = useRef(false);
  const { hasPermission, isCompanyFullAccess } = usePermission();
  const can = (p) => isCompanyFullAccess || hasPermission(p);
  const { accessToken } = useAccessToken();
  const [search, setSearch] = useState(initPage);
  const [companySalesData, setCompanySalesData] = useState([]);
  const [editCompanySalesData, setEditCompanySalesData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditSale, setIsEditSale] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [prepareDeleteData, setPrepareDeleteData] = useState({});
  const [companyCode, setCompanyCode] = useState("");
  const [total, setTotal] = useState(0);

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description) => {
    api[type]({
      message: message,
      description: description,
    });
  };

  const showModal = () => {
    setIsEditSale(false);
    setIsModalOpen(true);
    setEditCompanySalesData([]);
  };
  const handleOk = () => {
    setIsModalOpen(false);
    setIsEditSale(false);
    setEditCompanySalesData([]);
  };
  const handleCancel = () => {
    setEditCompanySalesData([]);
    setIsModalOpen(false);
    setIsEditSale(false);
  };

  const onDeleteCompanySale = async (data) => {
    let prepareDeleteCompanySale = {
      id: data.id,
    };
    setLoading(true);
    try {
      const response = await companyManagementService.deleteCompanySale(accessToken, prepareDeleteCompanySale);
      openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{"Delete sale success"}</>);
      setPrepareDeleteData({});
      setIsOpenDelete(false);
      fetchCompanySales();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      console.error("[CreateCompanySales] onDeleteCompanySale: ", err);
    }
  };

  const fetchCompanySales = useCallback(
    async (param) => {
      setLoading(true);
      const params = { ...param, company_id: detailData.id };
      let updateById = detailData.id;
      try {
        const data = await companyManagementService.getCompanySales(accessToken, updateById, params);
        setCompanySalesData(data?.querySale);
        setTotal(data.totalCount);
        setSearch({ ...param });
      } catch (err) {
        console.error("[CreateCompanySales] fetchCompanySales: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      }
      setLoading(false);
    },
    [accessToken]
  );

  useEffect(() => {
    if (!accessToken || !detailData?.id || hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    setCompanyCode(detailData);
    fetchCompanySales(search);
  }, [accessToken, detailData?.id]);

  const columns = [
    {
      title: t("Company_Create.Create_Sales.Table_No"),
      dataIndex: "id",
      key: "id",
    },
    {
      title: t("Company_Create.Create_Sales.Table_Sales_Code"),
      dataIndex: "sale_code",
      key: "sale_code",
    },
    {
      title: t("Company_Create.Create_Sales.Table_Sales_Name"),
      dataIndex: "sale_name",
      key: "sale_name",
    },
    {
      title: t("Company_Create.Create_Sales.Table_Customer_Count"),
      dataIndex: "app_customer",
      key: "app_customer",
      render: (_, record) => {
        const app_customer = record._count.app_customer ? record._count.app_customer : 0;
        return <p>{app_customer}</p>;
      },
    },
    {
      title: t("Company_Create.Create_Sales.Table_Email"),
      width: 300,
      dataIndex: "email",
      key: "email",
      render: (_, record) => {
        const email = record.email ? record.email : "-";
        return <p>{email}</p>;
      },
    },
    {
      title: t("Company_Create.Create_Sales.Table_Tel"),
      dataIndex: "telephone",
      key: "telephone",
      render: (_, record) => {
        const telephone = record.telephone || "-";
        return <p>{telephone}</p>;
      },
    },
    {
      title: t("Company_Table.Table_Edit"),
      dataIndex: "edit",
      key: "edit",
      render: (_, record) => (
        <Button
          type="link"
          disabled={!can(permissions.CompanySale.CHANGE)}
          onClick={() => {
            promiseAwait = new Promise((resolve) => {
              showModal();
              setIsEditSale(true);
              resolve(setEditCompanySalesData(record));
            });
          }}
        >
          <EditOutlined />
        </Button>
      ),
    },
    {
      title: t("Company_Table.Table_Delete"),
      dataIndex: "delete",
      key: "delete",
      render: (_, record) => (
        <Button
          type="link"
          disabled={!can(permissions.CompanySale.DELETE)}
          onClick={() => {
            setIsOpenDelete(true);
            setPrepareDeleteData(record);
          }}
        >
          <DeleteOutlined />
        </Button>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row align="middle" justify="end">
          <Col span={24}>
            <Table
              columns={columns}
              dataSource={companySalesData}
              scroll={{ x: "1440px", y: "500px" }}
              rowKey={(record) => record.id}
              pagination={{
                defaultPageSize: search.limit,
                current: search.page,
                pageSize: search.limit,
                total: total,
                onChange: (page, limit) => {
                  fetchCompanySales({ ...search, page, limit });
                },
                showSizeChanger: true,
              }}
              style={{ cursor: "pointer" }}
            />
          </Col>
          <Row justify="end" style={{ marginTop: "10px" }}>
            <Col span={24}>
              {can(permissions.CompanySale.ADD) && (
                <Button type="primary" onClick={showModal} style={{ marginRight: "10px", justifyContent: "end" }}>
                  {t("Button_Company.BTN_Add_Sales")}
                </Button>
              )}
            </Col>
          </Row>
          <ModelCreateSales
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            handleOk={handleOk}
            handleCancel={handleCancel}
            editCompanySalesData={editCompanySalesData}
            fetchCompanySales={fetchCompanySales}
            isEditSale={isEditSale}
            t={t}
            companyCode={companyCode}
          />
        </Row>
        <ModalDelete
          isOpen={isOpenDelete}
          handleCloseModal={() => {
            setIsOpenDelete(false);
          }}
          onClick={() => {
            onDeleteCompanySale(prepareDeleteData);
          }}
          dataDetail={prepareDeleteData}
          t={t}
        />
      </Spin>
    </>
  );
};
